import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, combineLatest } from 'rxjs';
import { tap, map, switchMap, take, filter } from 'rxjs/operators';
import { CaseTypeData, CaseTypeEffects } from '@protocols/ui';
import { AuthService } from '@telespot/web-core';
import { WorkspaceFormEffects } from '../../../stores/workspace-form.effects';
import { WorkspaceDetailEffects } from '../../../stores/workspace-detail.effects';

export interface EnvironmentOption {
  environment: string;
  enabled: boolean;
}

const ENVIRONMENTS = ['tiraspot'];

@Injectable()
export class WorkspaceEditorFacade extends ComponentStore<Record<string, never>> {
  private readonly organizationId: string;

  // Environment selectors
  public readonly environments$: Observable<EnvironmentOption[]>;
  public readonly draftEnvironment$: Observable<string | undefined>;

  // Combined selectors
  public readonly canSave$: Observable<boolean>;

  constructor(
    private readonly formEffects: WorkspaceFormEffects,
    private readonly detailEffects: WorkspaceDetailEffects,
    private readonly caseTypeEffects: CaseTypeEffects,
    readonly authService: AuthService,
    private readonly router: Router
  ) {
    super({});
    this.organizationId = this.authService.currentOrganizationValue?.id ?? '';

    this.environments$ = this.authService.currentOrganization$.pipe(
      map((organization) =>
        ENVIRONMENTS.map((env) => ({
          environment: env,
          enabled: organization?.license?.[env] ?? false,
        }))
      )
    );

    this.draftEnvironment$ = this.formEffects.draft$.pipe(
      map((draft) => (draft?.config?.['environment'] as string) ?? undefined)
    );

    this.canSave$ = combineLatest([this.formEffects.isValid$, this.formEffects.saving$]).pipe(
      map(([valid, saving]) => valid && !saving)
    );
  }

  // Delegated selectors from caseTypeEffects
  public get caseTypes$() {
    return this.caseTypeEffects.caseTypes$;
  }

  public get caseTypesLoading$() {
    return this.caseTypeEffects.loading$;
  }

  public get noCaseTypes$() {
    return this.caseTypeEffects.noCaseTypes$;
  }

  public get selectedCaseTypeId$() {
    return this.caseTypeEffects.selectedCaseTypeId$;
  }

  public get searchTerm$() {
    return this.caseTypeEffects.searchTerm$;
  }

  public get nextCaseTypeToken$() {
    return this.caseTypeEffects.nextCaseTypeToken$;
  }

  public get previousCaseTypeTokens$() {
    return this.caseTypeEffects.previousCaseTypeTokens$;
  }

  public get totalCount$() {
    return this.caseTypeEffects.totalCount$;
  }

  public get pageSize$() {
    return this.caseTypeEffects.pageSize$;
  }

  public get counting$() {
    return this.caseTypeEffects.counting$;
  }

  // Delegated selectors from form / detail effects
  public get draft$() {
    return this.formEffects.draft$;
  }

  public get isNewWorkspace$() {
    return this.formEffects.isNewWorkspace$;
  }

  public get saving$() {
    return this.formEffects.saving$;
  }

  public get savingError$() {
    return this.formEffects.savingError$;
  }

  public get loading$() {
    return this.detailEffects.loading$;
  }

  public get isValid$() {
    return this.formEffects.isValid$;
  }

  // Effects

  public readonly setSearchTerm = (searchTerm: string) => {
    this.caseTypeEffects.setSearchTerm(searchTerm);
  };

  public readonly loadMoreCaseTypes = (lastItem: CaseTypeData | undefined) => {
    this.caseTypeEffects.loadMoreCaseTypes(lastItem);
  };

  public readonly initializeForNew = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => {
        this.caseTypeEffects.initializeCaseTypes();
        this.formEffects.initializeNewWorkspace(this.organizationId);
      })
    )
  );

  public readonly initializeForEdit = this.effect((workspaceId$: Observable<string>) =>
    workspaceId$.pipe(
      tap((workspaceId: string) => {
        this.caseTypeEffects.initializeCaseTypes();
        this.formEffects.loadDraftFromDetail(workspaceId);
      }),
      switchMap(() =>
        this.draft$.pipe(
          filter((draft) => draft !== undefined),
          take(1),
          tap((draft) => {
            if (draft?.caseTypeId) {
              this.caseTypeEffects.selectCaseType(draft.caseTypeId);
            }
          })
        )
      )
    )
  );

  public readonly selectCaseType = this.effect((caseTypeId$: Observable<string>) =>
    caseTypeId$.pipe(
      tap((caseTypeId: string) => {
        this.caseTypeEffects.selectCaseType(caseTypeId);
        this.formEffects.updateDraft({ caseTypeId });
      })
    )
  );

  public readonly updateName = this.effect((name$: Observable<string>) =>
    name$.pipe(
      tap((name: string) => {
        this.formEffects.updateDraft({ name });
      })
    )
  );

  public readonly updateEnvironment = this.effect((environment$: Observable<string | undefined>) =>
    environment$.pipe(
      tap((environment) => {
        this.formEffects.updateDraft({
          config: environment ? { environment } : undefined,
        });
      })
    )
  );

  public readonly save = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => this.formEffects.save({ organizationId: this.organizationId })),
      switchMap(() =>
        this.formEffects.saveResult$.pipe(
          take(1),
          filter((result) => result.workspaceId !== undefined),
          tap((result) => {
            const workspaceId = result.workspaceId as string;
            // Refresh detail data with deferred counts (skip AI to avoid re-fetching it).
            this.detailEffects.loadDetail({ workspaceId });
            this.router.navigate(['/workspaces', workspaceId, 'members']);
          })
        )
      )
    )
  );

  public readonly cancel = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() =>
        combineLatest([this.isNewWorkspace$, this.formEffects.workspaceId$]).pipe(
          take(1),
          tap(([isNew, workspaceId]) => {
            if (isNew) {
              this.router.navigate(['/workspaces']);
            } else {
              this.router.navigate(['/workspaces', workspaceId, 'cases']);
            }
            this.formEffects.clearDraft();
            this.detailEffects.clearActiveWorkspace();
          })
        )
      )
    )
  );
}
